import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Grid,
    Typography,
    Chip,
    Divider,
    Stack,
    Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import BadgeIcon from "@mui/icons-material/Badge";
import EventNoteIcon from "@mui/icons-material/EventNote";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { setMainHeading } from "../../redux/modules/mainHeading/slice";

const StatsCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minHeight: 96,
}));

const IconWrapper = styled(Box)(({ theme, tone = "primary" }) => ({
    width: 56,
    height: 56,
    borderRadius: 12,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette[tone]?.light || theme.palette.primary.light,
    color: theme.palette[tone]?.main || theme.palette.primary.main,
    flexShrink: 0,
}));

const ListCard = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    borderRadius: 16,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    height: "100%",
    display: "flex",
    flexDirection: "column",
}));

const EncounterRow = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5, 1),
    borderRadius: 8,
    cursor: "pointer",
    transition: "background-color 150ms ease",
    "&:hover": {
        backgroundColor: theme.palette.action.hover,
    },
}));

const statusColor = {
    draft: "warning",
    finalized: "info",
    signed: "success",
};

const formatTime = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "numeric",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

const formatDate = (iso) => {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleDateString(undefined, {
            month: "short",
            day: "numeric",
        });
    } catch {
        return "";
    }
};

const EncounterList = ({ items, emptyText, showDate, onOpen }) => {
    if (!items?.length) {
        return (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <Typography variant="body2" color="text.secondary">{emptyText}</Typography>
            </Box>
        );
    }
    return (
        <Stack divider={<Divider flexItem />} spacing={0} sx={{ flex: 1 }}>
            {items.map((e) => (
                <EncounterRow key={e._id} onClick={() => onOpen(e)}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={600} noWrap>
                                {e.patientName || "Unknown patient"}
                                {e.patientMrn && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        MRN {e.patientMrn}
                                    </Typography>
                                )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block" }}>
                                {e.chiefComplaint || "No chief complaint recorded"}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                            <Typography variant="caption" color="text.secondary">
                                {showDate ? formatDate(e.visitDate) : formatTime(e.visitDate)}
                            </Typography>
                            <Chip
                                label={e.status}
                                size="small"
                                color={statusColor[e.status] || "default"}
                                sx={{ textTransform: "capitalize", fontWeight: 500 }}
                            />
                        </Box>
                    </Box>
                </EncounterRow>
            ))}
        </Stack>
    );
};

const AdminDashboard = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { currentBranch } = useSelector((state) => state.branch);
    const { userData } = useSelector((state) => state.auth);
    const [stats, setStats] = useState({
        totalPatientsCount: 0,
        totalStaffCount: 0,
        todaysEncountersCount: 0,
        pendingEncountersCount: 0,
        todaysEncounters: [],
        pendingEncounters: [],
    });

    useEffect(() => {
        dispatch(setMainHeading({ heading: "Dashboard" }));
    }, [dispatch]);

    useEffect(() => {
        if (!currentBranch?.id) return;
        const run = async () => {
            try {
                const url = `${process.env.REACT_APP_SERVER_API || ""}api/dashboard`;
                const res = await fetch(url, {
                    credentials: "include",
                    headers: { "X-Current-Branch": currentBranch.id },
                });
                if (!res.ok) return;
                const data = await res.json();
                if (data?.success) setStats(data.data);
            } catch (err) {
                console.error("Dashboard fetch failed", err);
            }
        };
        run();
    }, [currentBranch]);

    const openEncounter = (e) => {
        const role = userData?.role || "admin";
        navigate(`/${role}/patients/${e.patientId}/encounters/${e._id}`);
    };

    return (
        <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <IconWrapper><PeopleIcon /></IconWrapper>
                        <Box>
                            <Typography variant="h4">{stats.totalPatientsCount}</Typography>
                            <Typography variant="body2" color="text.secondary">Total patients</Typography>
                        </Box>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <IconWrapper><BadgeIcon /></IconWrapper>
                        <Box>
                            <Typography variant="h4">{stats.totalStaffCount}</Typography>
                            <Typography variant="body2" color="text.secondary">Active staff</Typography>
                        </Box>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <IconWrapper tone="info"><EventNoteIcon /></IconWrapper>
                        <Box>
                            <Typography variant="h4">{stats.todaysEncountersCount}</Typography>
                            <Typography variant="body2" color="text.secondary">Encounters today</Typography>
                        </Box>
                    </StatsCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard>
                        <IconWrapper tone="warning"><WarningAmberIcon /></IconWrapper>
                        <Box>
                            <Typography variant="h4">{stats.pendingEncountersCount}</Typography>
                            <Typography variant="body2" color="text.secondary">Drafts needing attention</Typography>
                        </Box>
                    </StatsCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ListCard>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                            <Typography variant="h6" fontWeight={600}>Today's encounters</Typography>
                            <Button
                                size="small"
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate(`/${userData?.role || "admin"}/patient-management`)}
                            >
                                All patients
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <EncounterList
                            items={stats.todaysEncounters}
                            emptyText="No encounters logged today yet."
                            onOpen={openEncounter}
                        />
                    </ListCard>
                </Grid>

                <Grid item xs={12} md={6}>
                    <ListCard>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                            <Typography variant="h6" fontWeight={600}>Drafts needing attention</Typography>
                            <Typography variant="caption" color="text.secondary">From prior days</Typography>
                        </Box>
                        <Divider sx={{ mb: 1 }} />
                        <EncounterList
                            items={stats.pendingEncounters}
                            emptyText="Nothing pending — all prior encounters are finalized."
                            showDate
                            onOpen={openEncounter}
                        />
                    </ListCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default AdminDashboard;
